<template>
	<modal-layout
		title="프로필 수정"
		id="modal-profile"
		size="lg"
		@show="onShow"
		@hide="onBeforeHide"
		@hidden="onHidden"
		:busy="signatureBusy || saving"
		:ok-disabled="signatureBusy || saving"
		okTitle="수정"
		@submit="onSubmit"
	>
		<b-form class="page-form" @submit.prevent="onSubmit">
			<fields
				title="사용자 아이디"
				type="text"
				isCol
				v-model="form.login_id"
				disabled
			/>
			<fields
				title="사용자 이름"
				type="text"
				isCol
				v-model="form.name"
				:disabled="info.permission !== 'ADMIN'"
			/>
			<fields
				title="사용자 이메일"
				type="text"
				isCol
				v-model="form.email"
				:disabled="info.permission !== 'ADMIN'"
			/>
			<fields
				title="사용자 직위"
				type="text"
				isCol
				v-model="form.rank"
			/>
			<fields
				title="새로운 비밀번호"
				type="password"
				isCol
				v-model="form.newPassword"
				name="new-password"
				autocomplete="new-password"
			/>
			<fields
				title="새로운 비밀번호 확인"
				type="password"
				isCol
				v-model="form.newPasswordConfirm"
				name="new-password-confirm"
				autocomplete="new-password"
			/>
			<fields
				title="사용자 소속"
				type="select"
				isCol
				v-model="form.dept_id"
				:options="dutySelectOptions"
				@select="option => form.dept_id = option.value"
				:disabled="info.permission !== 'ADMIN'"
			/>
			<fields
				title="사용자 권한"
				type="select"
				isCol
				v-model="form.permissionText"
				:options="permissionSelectOptions"
				@select="onChangePermission"
				:disabled="info.permission !== 'ADMIN'"
			/>
		</b-form>
		<signature-editor
			v-if="isOpen && form.id != null"
			ref="signatureEditor"
			:key="form.id"
			:user-id="form.id"
			:signature="signatureInfo"
			:upload="uploadSignature"
			:reload="reloadSignature"
			:disabled="saving"
			@busy="signatureBusy = $event"
			@pending="signaturePending = $event"
			@updated="signatureInfo = $event"
		/>
	</modal-layout>
</template>

<script>
import ModalLayout from '@/components/modal'
import SignatureEditor from '@/components/SignatureEditor.vue'
import api from '@/api'
import options from "@/utils/options"
import { mapGetters, mapActions } from 'vuex'

export default {
	name: 'ModalProfile',
	props: {
		info: {
			type: Object,
			default: () => {}
		}
	},
	components: {
		ModalLayout,
		SignatureEditor
	},
	watch: {
		info: {
			deep: true,
			handler() {
				// Store metadata updates must not overwrite the user's unsaved fields.
				if (!this.isOpen || this.form.id !== this.info?.id) this.fillForm()
			}
		}
	},
	data() {
		return {
			isOpen: false,
			signatureBusy: false,
			signaturePending: false,
			committingSignature: false,
			saving: false,
			signatureInfo: null,
			form: {
				id: null,
				login_id: "",
				email: "",
				name: "",
				phone: "",
				newPassword: "",
				permission: "",
				create_datetime: "",
				update_datetime: "",
				delete_datetime: "",
				dept_id: "",
				type_permission: null,
				rank: "",
				is_tech_license: "",
			},
			options: options
		}
	},
	computed: {
		...mapGetters('department', ['dutySelectOptions']),
		/** CS_SUB_ADMIN 등 UI 숨김 역할은 선택 목록에서 제외. 현재값이 숨김 역할이면 표시용으로만 포함. */
		permissionSelectOptions() {
			const rows = [...(this.options.permission || [])]
			const hidden = new Set(this.options.permissionHiddenFromUi || [])
			const p = this.form.permission
			if (
				p &&
				hidden.has(String(p)) &&
				!rows.some((o) => String(o.value) === String(p))
			) {
				const pt = this.options.permissionText || {}
				rows.push({ value: p, text: pt[p] != null ? pt[p] : String(p) })
			}
			return rows
		}
	},
	methods: {
		...mapActions('department', { loadDepartment: 'load' }),
		onShow() {
			this.loadDepartment()
			this.fillForm()
			this.signatureInfo = this.info?.signature || null
			this.isOpen = true
		},
		fillForm() {
			if(this.info?.name) {
				this.form = {...this.info}
				this.form.orgPassword = this.form.password;
				this.form.newPassword = '';
				this.form.newPasswordConfirm = ''
				const p = this.form.permission
				this.form.permissionText =
					this.options.permission.find(el => el.value === p)?.text
					|| this.options.permissionText?.[p]
					|| p
					|| ''
			}
		},
		onBeforeHide(event) {
			if (this.signatureBusy) { event.preventDefault(); return }
			// OK(수정) 버튼의 이벤트는 ok → hide 로 같은 객체가 넘어오므로 여기서 묻지 않는다 (제출은 onSubmit 이 처리).
			if (!event || event.defaultPrevented || event.trigger === 'ok') return
			if (this.signaturePending && !this.saving && event.trigger && !this.confirmDiscardSignature()) {
				event.preventDefault()
			}
		},
		confirmDiscardSignature() {
			if (typeof window === 'undefined' || typeof window.confirm !== 'function') return true
			return window.confirm('선택한 서명이 아직 저장되지 않았습니다. 저장하지 않고 닫을까요?')
		},
		onHidden() {
			this.isOpen = false
			this.signatureBusy = false
			this.signaturePending = false
			this.committingSignature = false
		},
		async reloadSignature(userId) {
			const profile = await api.user.getProfile()
			if (String(profile.id) !== String(userId)) throw new Error('사용자 정보가 변경되었습니다. 프로필을 다시 열어 주세요.')
			return profile.signature
		},
		uploadSignature(file) { return api.user.updateSignature(file) },
		onHide() {
			this.$nextTick(() => {
				this.$bvModal.hide('modal-profile')
			})
		},
		onChangePermission(option) {
			this.form.permission = option.value;
			this.form.permissionText = option.text;
		},
		onSubmit() {
			if (this.signatureBusy || this.saving || this.committingSignature) return
			if(this.form.newPassword&& this.form.newPassword !== this.form.newPasswordConfirm) {
				this.$toast.error('새로운 비밀번호와 비밀번호 확인이 다릅니다. 비밀번호를 다시 확인해주세요')
				return false;
			}
			// 서명 파일이 선택돼 있으면 먼저 업로드하고, 실패하면 프로필 저장을 시작하지 않는다 (2026-09-20).
			const editor = this.$refs.signatureEditor
			if (editor && typeof editor.commitPending === 'function' && typeof editor.hasPendingFile === 'function' && editor.hasPendingFile()) {
				this.committingSignature = true
				return editor.commitPending().then((ok) => {
					this.committingSignature = false
					if (!ok) {
						this.$toast?.error?.(`선택한 서명을 저장하지 못해 수정을 중단했습니다. ${editor.error || '서명 영역의 안내를 확인한 뒤 다시 수정해 주세요.'}`)
						return false
					}
					return this.emitSubmit()
				}).catch(() => { this.committingSignature = false; return false })
			}
			return this.emitSubmit()
		},
		emitSubmit() {
			if (this.saving) return
			this.saving = true
			const payload = { ...this.form }
			delete payload.newPasswordConfirm
			this.$emit('submit', payload, () => { this.saving = false })
		}
	}
}
</script>
